import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Copy, Mail, Pencil, ExternalLink, Ban, CheckCircle2, Trash2, Loader2 } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { Dot, Tag, Fact } from '../ui'
import { healthChecks, formatDate, formatRelative, studioUrl } from '../../lib/support'

// Sticky summary rail of the tenant workspace: identity, health check, facts, actions.
export const TenantRail = ({ view, onTab, onToggleStatus, onSendReset, sendingReset, onPurge, statusBusy }) => {
  const navigate = useNavigate()
  const { isOwner } = useAuth()
  const [copied, setCopied] = useState(false)
  const m = view.model

  const copySlug = () => {
    navigator.clipboard?.writeText(m.slug).catch(() => {})
    setCopied(true)
    setTimeout(() => setCopied(false), 1400)
  }

  const owner = m.owner
  const facts = [
    ['Proprietário', m.noOwner ? 'Não definido' : owner ? [owner.name, owner.email].filter(Boolean).join(' · ') : m.ownerEmail],
    ['E-mail de cobrança', m.billingEmail],
    ['Domínio', m.domain],
    ['Fuso horário', m.timezone],
    ['Criado em', formatDate(m.createdAt)],
    ['Última atividade', m.lastActive ? formatRelative(m.lastActive) : '—'],
  ]

  return (
    <aside className="card flex flex-col gap-4 p-4 lg:sticky lg:top-[76px]">
      <button type="button" className="btn btn-ghost self-start" onClick={() => navigate('/tenants')}>
        <ArrowLeft className="h-4 w-4" />
        Estúdios
      </button>

      <div className="flex flex-col gap-1.5">
        <h3 className="text-lg">{m.name}</h3>
        <button
          type="button"
          onClick={copySlug}
          className="mono flex items-center gap-1.5 self-start text-xs text-admin-600"
          aria-label={`Copiar slug ${m.slug}`}
        >
          {m.slug}
          <Copy className="h-3 w-3" />
          <span className="text-green-800" aria-live="polite">
            {copied ? 'Copiado' : ''}
          </span>
        </button>
        <div className="mt-1 flex flex-wrap gap-1.5">
          <Tag tone={view.status.tone}>{view.status.label}</Tag>
          <Tag tone="muted">{m.plan}</Tag>
          <Tag tone={view.billing.tone}>{view.billing.label}</Tag>
        </div>
      </div>

      <div className="flex flex-col gap-0.5">
        <span className="micro-label mb-1">Verificação de saúde</span>
        {healthChecks(view).map((c) => (
          <button
            key={c.label}
            type="button"
            onClick={() => onTab(c.tab)}
            className="-mx-2 grid grid-cols-[8px_104px_minmax(0,1fr)] items-center gap-2.5 rounded-md px-2 py-[5px] text-left text-[13px] hover:bg-admin-50"
          >
            <Dot tone={c.tone} />
            <span className="text-admin-600">{c.label}</span>
            <span className="truncate" title={c.value}>
              {c.value}
            </span>
          </button>
        ))}
      </div>

      <div className="flex flex-col gap-2">
        <span className="micro-label">Contato e dados</span>
        {facts.map(([label, value]) => (
          <Fact key={label} label={label}>
            {value || '—'}
          </Fact>
        ))}
      </div>

      <div className="flex flex-col gap-1.5">
        {isOwner && (
          <>
            <button
              type="button"
              className="btn btn-secondary justify-start"
              onClick={onSendReset}
              disabled={sendingReset || m.noOwner}
              aria-label="Enviar link de redefinição de senha ao proprietário"
            >
              {sendingReset ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
              {sendingReset ? 'Enviando link de redefinição…' : 'Enviar link de redefinição ao proprietário'}
            </button>
            <button type="button" className="btn btn-secondary justify-start" onClick={() => navigate(`/tenants/${m.id}/edit`)}>
              <Pencil className="h-4 w-4" />
              Editar estúdio
            </button>
          </>
        )}
        <a className="btn btn-secondary justify-start" href={studioUrl(m)} target="_blank" rel="noopener noreferrer">
          <ExternalLink className="h-4 w-4" />
          Abrir estúdio
        </a>
        <button type="button" className="btn btn-ghost justify-start" onClick={onToggleStatus} disabled={statusBusy}>
          {m.active ? <Ban className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
          {m.active ? 'Suspender estúdio' : 'Ativar estúdio'}
        </button>
        {isOwner && (
          <button type="button" className="btn btn-ghost justify-start text-red-700 hover:bg-red-50" onClick={onPurge}>
            <Trash2 className="h-4 w-4" />
            Excluir estúdio…
          </button>
        )}
      </div>
    </aside>
  )
}
